// 📄 src/api/campaignReportApi.js
import axiosClient from "./axiosClient";

/**
 * IMPORTANT:
 * I don’t hardcode ONE endpoint because your backend naming evolved.
 * So we try a couple of likely routes (same pattern you already use in teamStaffApi). :contentReference[oaicite:9]{index=9}
 */

async function tryGet(paths, params, config = {}) {
  let lastErr;
  for (const p of paths) {
    const cleanPath = p.startsWith("/") ? p.slice(1) : p;
    try {
      return await axiosClient.get(cleanPath, {
        __silent: config.__silent ?? true,
        ...config,
        params,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405) {
        lastErr = err;
        continue;
      }
      throw err;
    }
  }
  throw lastErr ?? new Error("No campaign report endpoint responded.");
}

export async function getCampaignReportSummary(campaignId, opts = {}) {
  const { repliedWindowDays = 7, windowDays } = opts;
  const hasExplicitAllWindow =
    windowDays === null ||
    windowDays === undefined ||
    String(windowDays).trim().toLowerCase() === "all";
  const days = hasExplicitAllWindow
    ? null
    : Number.isFinite(Number(windowDays))
      ? Number(windowDays)
      : Number(repliedWindowDays);
  const params = {};

  if (days != null) {
    params.repliedWindowDays = days;
    params.windowDays = days;
  }

  return tryGet(
    [
      // Most stable legacy endpoint (used across the app)
      `campaign-logs/campaign/${campaignId}/summary`,
      `CampaignTracking/campaign/${campaignId}/summary`,
      `CampaignReports/campaign/${campaignId}/summary`,
      `CampaignReport/campaign/${campaignId}/summary`,
      `campaign-tracking/campaign/${campaignId}/summary`,
      `campaign-reports/campaign/${campaignId}/summary`,
      `campaign-report/campaign/${campaignId}/summary`,
    ],
    params,
    { __silent: true },
  );
}

function normalizeBucket(bucket) {
  const b = String(bucket || "")
    .trim()
    .toLowerCase();
  if (!b) return b;
  // frontend uses "SENT"/"DELIVERED"/...; backend expects lowercase query values
  if (b === "sent") return "sent";
  if (b === "delivered") return "delivered";
  if (b === "read") return "read";
  if (b === "clicked" || b === "url_clicked") return "url_clicked";
  if (b === "button_clicked") return "button_clicked";
  if (b === "replied") return "replied";
  if (b === "failed") return "failed";
  return b;
}

/**
 * bucket:
 *  - "DELIVERED_NOT_REPLIED"
 *  - "READ_NOT_REPLIED"
 *  - "FAILED"
 *  - "REPLIED"
 */
export async function getCampaignBucketContacts(campaignId, bucket, opts = {}) {
  const {
    q = "",
    page = 1,
    pageSize = 50,
    repliedWindowDays = 7,
    fromUtc,
    toUtc,
    runId,
  } = opts;

  const bucketValue = normalizeBucket(bucket);
  const params = {
    bucket: bucketValue,
    search: q,
    fromUtc,
    toUtc,
    repliedWindowDays,
    runId,
    page,
    pageSize,
  };

  // Keep only backend-supported query params.
  Object.keys(params).forEach(k => {
    if (params[k] == null || params[k] === "") delete params[k];
  });

  return axiosClient.get(`campaign-logs/campaign/${campaignId}/contacts`, {
    __silent: true,
    params,
  });
}

export async function previewCampaignRetargetAudience(campaignId, payload) {
  const body = payload && typeof payload === "object" ? { ...payload } : {};
  if (body.campaignId == null) body.campaignId = campaignId;
  if (body.sourceCampaignId == null) body.sourceCampaignId = campaignId;

  return axiosClient.post("campaigns/retarget/preview", body, {
    __silent: true,
  });
}
